<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import AiIdentity from 'dashboard/components-next/ai/AiIdentity.vue';
import AiUsageReceipt from 'dashboard/components-next/ai/AiUsageReceipt.vue';
import { AI_NAME } from 'dashboard/helper/aiIdentity';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  generate: { type: Function, required: true },
  context: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['apply']);
const { t } = useI18n();
const { isAdmin } = useAdmin();
const prompt = ref('');
const audience = ref('');
const tone = ref('professional');
const generationMode = ref('detailed');
const temperature = ref(0.8);
const isGenerating = ref(false);
const draft = ref(null);
const usage = ref(null);
const generationOptions = computed(() => ({
  ...props.context,
  prompt: prompt.value.trim(),
  generation_mode: generationMode.value,
  tone: tone.value,
  audience: audience.value.trim(),
  ...(generationMode.value === 'creative'
    ? { temperature: temperature.value }
    : {}),
}));
const contextKey = computed(() =>
  JSON.stringify({ ...generationOptions.value, temperature: temperature.value })
);
let contextVersion = 0;
watch(
  contextKey,
  () => {
    contextVersion += 1;
    draft.value = null;
  },
  { flush: 'sync' }
);
const modeOptions = computed(() => [
  { value: 'detailed', label: t('AI_TEMPLATE_DRAFT.MODES.DETAILED') },
  { value: 'creative', label: t('AI_TEMPLATE_DRAFT.MODES.CREATIVE') },
]);
const toneOptions = computed(() => [
  { value: 'professional', label: t('AI_TEMPLATE_DRAFT.TONES.PROFESSIONAL') },
  { value: 'friendly', label: t('AI_TEMPLATE_DRAFT.TONES.FRIENDLY') },
  { value: 'direct', label: t('AI_TEMPLATE_DRAFT.TONES.DIRECT') },
  { value: 'persuasive', label: t('AI_TEMPLATE_DRAFT.TONES.PERSUASIVE') },
]);
const validTemperature = computed(
  () =>
    Number.isFinite(temperature.value) &&
    temperature.value >= 0 &&
    temperature.value <= 2 &&
    Math.abs(temperature.value * 10 - Math.round(temperature.value * 10)) < 1e-9
);
const canGenerate = computed(
  () =>
    !isGenerating.value &&
    !props.disabled &&
    prompt.value.trim().length >= 10 &&
    prompt.value.trim().length <= 3000 &&
    audience.value.trim().length <= 300 &&
    (generationMode.value !== 'creative' || validTemperature.value)
);

const generateDraft = async () => {
  if (!canGenerate.value) return;
  const requestedVersion = contextVersion;
  isGenerating.value = true;
  draft.value = null;
  usage.value = null;
  try {
    const { data } = await props.generate(generationOptions.value);
    usage.value = data.usage;
    if (requestedVersion !== contextVersion) {
      useAlert(t('AI_TEMPLATE_DRAFT.CHANGED'));
      return;
    }
    draft.value = data.draft;
  } catch (error) {
    if (error?.response?.data?.usage) usage.value = error.response.data.usage;
    useAlert(error?.response?.data?.error || t('AI_TEMPLATE_DRAFT.ERROR'));
  } finally {
    isGenerating.value = false;
  }
};

const applyDraft = () => {
  if (!draft.value || props.disabled || isGenerating.value) return;
  emit('apply', draft.value);
  draft.value = null;
  useAlert(t('AI_TEMPLATE_DRAFT.APPLIED'));
};
</script>

<template>
  <section
    class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
  >
    <div class="flex items-start gap-3">
      <AiIdentity class="size-8 shrink-0" :animated="isGenerating" />
      <div>
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ t('AI_TEMPLATE_DRAFT.TITLE', { name: AI_NAME }) }}
        </h3>
        <p class="mb-0 mt-1 text-xs text-n-slate-11">
          {{ t('AI_TEMPLATE_DRAFT.DESCRIPTION') }}
        </p>
      </div>
    </div>
    <NextTextArea
      v-model="prompt"
      :label="t('AI_TEMPLATE_DRAFT.PROMPT')"
      :placeholder="t('AI_TEMPLATE_DRAFT.PLACEHOLDER')"
      :max-length="3000"
      :disabled="isGenerating || disabled"
      show-character-count
      resize
    />
    <div class="grid min-w-0 gap-3 sm:grid-cols-2">
      <NextInput
        v-model="audience"
        :label="t('AI_TEMPLATE_DRAFT.AUDIENCE')"
        :placeholder="t('AI_TEMPLATE_DRAFT.AUDIENCE_PLACEHOLDER')"
        maxlength="300"
        :disabled="isGenerating || disabled"
        data-testid="ai-template-audience"
      />
      <label class="grid min-w-0 gap-1 text-heading-3 text-n-slate-12">
        {{ t('AI_TEMPLATE_DRAFT.TONE') }}
        <NextSelect
          v-model="tone"
          :options="toneOptions"
          :aria-label="t('AI_TEMPLATE_DRAFT.TONE')"
          :disabled="isGenerating || disabled"
          class="!w-full [&>select]:w-full"
          data-testid="ai-template-tone"
        />
      </label>
    </div>
    <div class="grid min-w-0 gap-3 rounded-lg bg-n-alpha-1 p-3">
      <label class="grid min-w-0 gap-1 text-heading-3 text-n-slate-12">
        {{ t('AI_TEMPLATE_DRAFT.MODE') }}
        <NextSelect
          v-model="generationMode"
          :options="modeOptions"
          :aria-label="t('AI_TEMPLATE_DRAFT.MODE')"
          :disabled="isGenerating || disabled"
          class="!w-full [&>select]:w-full"
          data-testid="ai-template-mode"
        />
      </label>
      <p class="m-0 text-xs text-n-slate-11">
        {{
          generationMode === 'detailed'
            ? t('AI_TEMPLATE_DRAFT.DETAILED_HINT')
            : t('AI_TEMPLATE_DRAFT.CREATIVE_HINT')
        }}
      </p>
      <div v-if="generationMode === 'creative'" class="grid gap-2">
        <div
          class="grid min-w-0 grid-cols-[minmax(0,1fr)_6rem] items-end gap-3"
        >
          <input
            v-model.number="temperature"
            type="range"
            min="0"
            max="2"
            step="0.1"
            :aria-label="t('AI_TEMPLATE_DRAFT.TEMPERATURE')"
            :disabled="isGenerating || disabled"
            class="my-3 h-4 w-full accent-n-brand disabled:opacity-50"
            data-testid="ai-template-temperature-range"
          />
          <NextInput
            v-model="temperature"
            type="number"
            :min="0"
            :max="2"
            step="0.1"
            :label="t('AI_TEMPLATE_DRAFT.TEMPERATURE')"
            :disabled="isGenerating || disabled"
            data-testid="ai-template-temperature"
          />
        </div>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('AI_TEMPLATE_DRAFT.TEMPERATURE_HINT') }}
        </p>
      </div>
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <NextButton
        :label="t('AI_TEMPLATE_DRAFT.GENERATE')"
        size="sm"
        :is-loading="isGenerating"
        :disabled="!canGenerate"
        @click="generateDraft"
      />
      <span v-if="isAdmin" class="text-xs text-n-slate-11">{{
        t('AI_TEMPLATE_DRAFT.USAGE_HINT')
      }}</span>
    </div>
    <div v-if="draft" class="flex flex-col gap-2 rounded-lg bg-n-alpha-1 p-3">
      <strong class="break-words text-sm text-n-slate-12">{{
        draft.name
      }}</strong>
      <span
        v-if="draft.header"
        class="break-words text-sm font-medium text-n-slate-12"
        >{{ draft.header }}</span
      >
      <p class="m-0 whitespace-pre-wrap break-words text-sm text-n-slate-12">
        {{ draft.body }}
      </p>
      <span v-if="draft.footer" class="break-words text-xs text-n-slate-11">{{
        draft.footer
      }}</span>
      <div v-if="draft.button_texts?.length" class="flex flex-wrap gap-2">
        <span
          v-for="text in draft.button_texts"
          :key="text"
          class="rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-12"
          >{{ text }}</span
        >
      </div>
      <span v-if="draft.action_text" class="text-xs text-n-slate-11">{{
        t('AI_TEMPLATE_DRAFT.ACTION', { text: draft.action_text })
      }}</span>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('AI_TEMPLATE_DRAFT.REVIEW') }}
      </p>
      <NextButton
        :label="t('AI_TEMPLATE_DRAFT.APPLY')"
        size="sm"
        outline
        :disabled="disabled || isGenerating"
        @click="applyDraft"
      />
    </div>
    <AiUsageReceipt v-if="usage" :usage="usage" />
  </section>
</template>
